import type {
  ServiceDefinition,
  Step,
  ServiceCategoryItem,
  PricingRow,
  InfoSection,
  FAQ,
} from "../types";

// ---------------------------------------------------------------------------
// Shared content across all bortforsling keyword variants
// ---------------------------------------------------------------------------

const steps: Step[] = [
  {
    number: 1,
    title: "Boka din bortforsling",
    description:
      "Ange din adress, fyll i information, och boka en tid som passar dig. Du ser priset och kan boka direkt online.",
  },
  {
    number: 2,
    title: "Vi hämtar och forslar bort allt",
    description:
      "Våra ungdomar kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus och trånga hissar – du behöver inte lyfta ett finger.",
  },
  {
    number: 3,
    title: "Allt bortforslat",
    description:
      "Vi kör allt till närmsta avfallsstation eller återvinningscentral. Du slipper tänka på sortering och transport.",
  },
];

const categories: ServiceCategoryItem[] = [
  {
    image: "/recycling-bin.webp",
    name: "Återvinning",
    desc: "Glas, metall, papper och annat återvinningsbart",
  },
  {
    image: "/box.webp",
    name: "Kartonger",
    desc: "Flyttkartonger och wellpappkartonger",
  },
  {
    image: "/furniture.webp",
    name: "Möbler",
    desc: "Vardagsrum, sovrum, kontor och andra hemmöbler",
  },
  {
    image: "/electronics.webp",
    name: "Elektronik",
    desc: "TV-apparater, datorer, ljudsystem och annan elektronik",
  },
  {
    image: "/appliances.webp",
    name: "Vitvaror",
    desc: "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
  },
  {
    image: "/fitness-equipment.webp",
    name: "Träningsutrustning",
    desc: "Löpband, cyklar, vikter och annan träningsutrustning",
  },
];

const pricing: PricingRow[] = [
  {
    name: "Small",
    desc: "1–2 större föremål som soffa eller kylskåp",
    image: "/service-things-small.webp",
    crew: "2 pers + skåpbil",
    framkorning: "20 km ingår, sedan 8 kr/km",
    avgCost: "995 kr",
  },
  {
    name: "Medium",
    desc: "Flera möbler eller skrymmande föremål",
    image: "/service-room-medium.webp",
    crew: "2 pers + skåpbil",
    framkorning: "20 km ingår, sedan 12 kr/km",
    avgCost: "1 495 kr",
  },
  {
    name: "Large",
    desc: "Tömning av ett rum eller liten lägenhet",
    image: "/service-space-large.webp",
    crew: "2 pers + skåpbil",
    framkorning: "20 km ingår, sedan 15 kr/km",
    avgCost: "2 495 kr",
  },
  {
    name: "X-Large",
    desc: "Komplett tömning av bostad eller kontor",
    image: "/service-whole-place-x-large.webp",
    crew: "2 pers + skåpbil",
    framkorning: "20 km ingår, sedan 18 kr/km",
    avgCost: "4 995 kr",
  },
];

const infoSections: InfoSection[] = [
  {
    heading: "Bortforsling",
    content:
      "Att bli av med skrymmande möbler, elektronik eller ett helt bohag behöver inte vara krångligt. Med Ungdomskraft får du snabb och smidig bortforsling – oavsett om det handlar om en enstaka soffa, ett helt dödsbo eller ett förråd som behöver tömmas. Vi tar hand om allt från tunga lyft till ansvarsfull sortering och återvinning, så att du slipper köra till tippen själv.\n\nVåra unga, energiska medarbetare hämtar ditt skräp direkt från hemmet, kontoret eller förrådet. Du visar bara vad som ska bort – vi bär, lastar och kör iväg allt åt dig.",
  },
  {
    heading: "Varför boka bortforsling med Ungdomskraft?",
    listItems: [
      {
        label: "Enkel bokning utan krångel -",
        text: "Boka din bortforsling på några minuter. Fyll i adress, välj datum och få pris direkt – slipp väntan på offert. Behöver du hjälp redan idag? Vi kan vara på plats redan inom 60 minuter för akuta behov. Självklart kan du även ringa in och boka på 08-517 082 81.",
      },
      {
        label: "Flexibla tider som passar dig -",
        text: "Vi vet att din tid är värdefull. Därför erbjuder vi bortforsling på tider som passar ditt schema – vardagar, kvällar och helger. Välj det datum och den tid som fungerar bäst för dig.",
      },
      {
        label: "Miljövänlig hantering och återvinning -",
        text: "Vi sorterar och återvinner så mycket som möjligt. Det som kan doneras skänks vidare till organisationer som Myrorna och Stadsmissionen. Resten hanteras ansvarsfullt och körs till godkänd återvinningsstation. Du kan även ha rätt till RUT-avdrag när vi transporterar saker till återanvändning.",
      },
      {
        label: "Försäkrat och tryggt -",
        text: "Alla uppdrag är försäkrade genom Trygg Hansa. Du kan känna dig trygg med att dina ägodelar och din fastighet är skyddade under hela processen – från lyft till transport.",
      },
    ],
  },
  {
    heading: "Vad kan vi forsla bort?",
    content:
      "Vi hanterar de flesta typer av skrymmande föremål och avfall. Oavsett om du behöver bli av med enstaka saker eller tömma ett helt utrymme hjälper vi dig:",
    listItems: [
      { label: "Möbler -", text: "soffor, sängar, bord, stolar, bokhyllor och garderober" },
      { label: "Vitvaror -", text: "tvättmaskiner, torktumlare, kylskåp, frysar och spisar" },
      { label: "Elektronik -", text: "TV-apparater, datorer, skärmar och mindre elavfall" },
      { label: "Grovsopor -", text: "madrasser, mattor, lampor och annat som inte ryms i vanliga sopkärlet" },
      { label: "Trädgårdsavfall -", text: "grenar, jord, sten och övrigt trädgårdsskräp" },
      { label: "Byggavfall -", text: "rivningsmaterial, gipsskivor och skräp efter mindre renoveringar" },
      { label: "Kontorsinventarier -", text: "skrivbord, kontorsstolar, arkivskåp och IT-utrustning" },
    ],
    afterContent:
      "Vi tar även emot blandade lass där allt möjligt ska bort samtidigt. Är du osäker på om vi kan hantera just ditt avfall? Hör av dig så hjälper vi dig.",
  },
  {
    heading: "Så fungerar det – bortforsling i tre enkla steg",
    listItems: [
      {
        label: "1. Beskriv ditt behov -",
        text: "Fyll i vårt bokningsformulär med adress, vad som ska forslas bort och önskat datum. Du får ett pris direkt – inga dolda kostnader.",
      },
      {
        label: "2. Vi kommer till dig -",
        text: "Våra ungdomar dyker upp på överenskommen tid, bär ut allt och lastar på bilen. Vi hanterar trapphus, hissar och trånga utrymmen – du behöver inte lyfta ett finger.",
      },
      {
        label: "3. Ansvarsfull hantering -",
        text: "Vi kör ditt avfall till rätt ställe. Det som går att återanvända skänks vidare, resten sorteras och lämnas på godkänd återvinningsstation. Allt hanteras enligt gällande regler så att så lite som möjligt hamnar på deponi.",
      },
    ],
  },
  {
    heading: "Erfarna bortforslare med lokalkännedom",
    content:
      "Våra ungdomar har gedigen erfarenhet av bortforsling. De är vana vid trånga trapphus, smala hissar och innergårdar som kräver extra planering. Oavsett om du bor i en villa, en lägenhet eller ett radhus vet de hur uppdraget ska genomföras på ett effektivt och säkert sätt.\n\nVi utför dagligen bortforsling av allt från enstaka möbler till kompletta lägenhetsröjningar och dödsbon. Den erfarenheten gör att vi kan ge dig ett exakt pris redan vid bokning och genomföra uppdraget utan fördröjningar.",
  },
  {
    heading: "Mer än bara bortforsling – du gör skillnad",
    content:
      "Det som gör Ungdomskraft unikt är att vi sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet. När du bokar bortforsling hos oss får du inte bara professionell hjälp med att hämta skräp och köra till tippen – du bidrar också till att unga människor kommer ut i arbetslivet och bygger sin framtid.\n\nVarje uppdrag bemannas av motiverade ungdomar som får lära sig allt från kundkontakt och logistik till fysiskt arbete och ansvarsfullt miljöarbete. Det är bortforsling som gör skillnad, varje bokning räknas.",
  },
  {
    heading: "Boka bortforsling idag",
    content:
      'Letar du efter bortforsling nära mig och vill ha en tjänst som kombinerar smidighet, pålitlighet och bra pris? Då är Ungdomskraft rätt val. Vi gör det enkelt att bli av med det du inte längre behöver – snabbt, tryggt och miljövänligt.\n\nFyll i formuläret så får du pris direkt. Inga offertförfrågningar – bara enkel bortforsling som dessutom gör skillnad. Självklart kan du även ringa in och boka bortforsling på 08-517 082 81.',
    cta: { text: "Boka bortforsling", href: "#priser" },
  },
];

const faqs: FAQ[] = [
  {
    q: "Vilka typer av föremål kan ni hantera vid bortforsling?",
    a: "Vi kan forsla bort nästan allt – från gamla möbler, madrasser och vitvaror till kartonger, trädgårdsavfall och allmänt hushållsskräp. Vi tar även skrymmande föremål som soffor, bokhyllor och träningsutrustning. Oavsett om det gäller en garageröjning, kontorsröjning eller en hel lägenhetsröjning gör våra ungdomar bortforslingen snabb och smidig. Av säkerhetsskäl kan vi inte hantera farligt avfall som kemikalier, färg eller asbest.",
  },
  {
    q: "Kan ni bortforsla saker direkt från mitt hem (inomhus), eller måste de stå utomhus?",
    a: "Ja, våra ungdomar hämtar dina saker var de än står – inne i bostaden, på vinden, i källaren eller i garaget. Du behöver inte bära ut något själv. Vi är vana vid trånga trapphus, smala hissar och svåråtkomliga utrymmen.",
  },
  {
    q: "Vad händer med mina saker efter bortforslingen?",
    a: "Vi sorterar allt vi forslar bort. Det som fortfarande är i bra skick skänks vidare till organisationer som Myrorna eller Stadsmissionen. Resten lämnas på godkänd återvinningsstation där materialet sorteras och återvinns enligt gällande miljöregler. Vårt mål är alltid att så lite som möjligt hamnar på deponi.",
  },
  {
    q: "Kan ni bortforsla madrasser, elektronik eller vitvaror?",
    a: "Absolut. Vi hämtar och forslar bort madrasser, TV-apparater, datorer, tvättmaskiner, kylskåp, frysar och andra vitvaror. Elektronik och vitvaror sorteras ut separat och lämnas till godkänd elektronikåtervinning. Eventuella avfallsavgifter på återvinningsstationen kan tillkomma beroende på föremålstyp.",
  },
  {
    q: "Hur ska jag förbereda mig inför min bortforsling?",
    a: "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen kan du rensa en fri väg (ca 1 meter bred) från entrén till föremålen och om möjligt samla sakerna på ett ställe. Det minskar arbetstiden och håller nere kostnaden.",
  },
  {
    q: "Erbjuder ni bortforsling samma dag eller nästa dag?",
    a: "Ja, vi erbjuder både samdags- och nästadagsservice. Du kan boka våra ungdomar med så kort varsel som 30 minuter eller planera upp till 30 dagar i förväg. Välj helt enkelt den tid som passar dig bäst i bokningsformuläret.",
  },
  {
    q: "Kan ni montera isär möbler vid bortforsling?",
    a: "Ja, våra ungdomar hjälper gärna till med enklare isärmontering i samband med din bortforsling. De har med sig standardverktyg, men om dina möbler kräver specialverktyg eller instruktioner ber vi dig ha dessa redo. Tid för isärmontering ingår i din totala arbetstid.",
  },
  {
    q: "Kan ni skänka mina saker istället för att bortforsla dem?",
    a: "Ja! Om dina saker fortfarande är i bra skick kan vi leverera dem till en secondhand-butik eller välgörenhetsorganisation istället för att köra dem till tippen. Ange bara önskad inlämningsplats vid bokning. Vi rekommenderar att du kontaktar mottagaren i förväg för att bekräfta att de kan ta emot dina saker. Transport till återanvändning kan dessutom ge dig rätt till RUT-avdrag (25 %).",
  },
  {
    q: "Ingår avfallsavgifter i priset för min bortforsling?",
    a: "Nej, avfalls- och deponiavgifter ingår inte i priset utan debiteras separat. Avgiften varierar beroende på återvinningsstation samt vikten och typen av föremål, men ligger oftast runt 400 kr. Du ser alltid den totala kostnaden innan vi påbörjar uppdraget, så att du vet exakt vad det kostar.",
  },
  {
    q: "Kan jag få RUT-avdrag på bortforsling?",
    a: "Det beror på vad som händer med sakerna. Om dina föremål transporteras till en secondhand-butik, välgörenhetsorganisation eller annan plats för återanvändning har du rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där sakerna körs direkt till återvinningsstation eller deponi ger däremot inte rätt till RUT-avdrag enligt Skatteverkets regler. Vi hjälper dig gärna att avgöra vad som gäller för just ditt uppdrag – ange gärna vid bokning om du vill att sakerna ska skänkas så planerar vi upplägget därefter. Avdraget hanteras direkt på fakturan, så du behöver aldrig ligga ute med pengarna.",
  },
  {
    q: "Vad kostar bortforsling?",
    a: "Priset beror på mängden, typen av föremål och hur lång tid uppdraget tar. Du får ett pris direkt i vår bokning baserat på vad du anger – inga dolda avgifter eller överraskningar i efterhand. Avfallsavgifter på återvinningsstationen tillkommer separat och ligger oftast runt 400 kr. Vill du ha en exakt uppskattning för ett större uppdrag? Kontakta oss så återkommer vi med en skräddarsydd offert.",
  },
  {
    q: "Hur lång tid tar en bortforsling?",
    a: "Det varierar beroende på mängden och hur tillgängliga föremålen är. En enklare bortforsling av ett par möbler tar vanligtvis 30–60 minuter. Större uppdrag som en lägenhetsröjning eller dödsbotömning kan ta en halv till en hel dag. Våra ungdomar jobbar effektivt och är vana vid att bära tungt – vi ser till att det går så snabbt som möjligt utan att tumma på kvaliteten.",
  },
  {
    q: "Kan ni hjälpa med dödsbo eller lägenhetsröjning?",
    a: "Ja, vi har gedigen erfarenhet av att tömma dödsbon och hela lägenheter. Våra ungdomar hanterar allt från möbler och personliga tillhörigheter till grovsopor och elektronik. Vi sorterar noggrant – det som fortfarande är i bra skick skänks vidare och resten lämnas på godkänd återvinningsstation. Du behöver inte vara på plats om du inte vill, vi sköter hela processen åt dig.",
  },
  {
    q: "Vilka områden erbjuder ni bortforsling i?",
    a: "Vi erbjuder bortforsling i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö. Hittar du inte ditt område? Hör av dig så berättar vi om vi kan hjälpa dig.",
  },
  {
    q: "Vem ordnar parkering för lastbilen?",
    a: "Vi tar hand om det. Våra ungdomar är vana vid att hitta lösningar för parkering i trånga bostadsområden och innerstadsgator. Om det krävs parkeringstillstånd eller särskilt tillträde meddelar vi dig i förväg. Du behöver inte tänka på logistiken – det ingår i tjänsten.",
  },
];

// ---------------------------------------------------------------------------
// Keyword variants
// ---------------------------------------------------------------------------

export const bortforsling: ServiceDefinition = {
  id: "bortforsling",
  name: "Bortforsling",
  keywords: [
    {
      slug: "bortforsling",
      keyword: "Bortforsling",
      headingPrefix: "Hjälp med",
      headingKeyword: "bortforsling {{kommun}}",
      subtext:
        "Boka bortforsling {{kommun}} av möbler, vitvaror och skrymmande föremål – snabbt och enkelt. Vi skickar ungdomar med lastbil direkt till din adress, ofta samma dag. Fast pris, enkel bokning och över 50 000 slutförda uppdrag.",
      heroImage: "/hero-bortforsling.webp",
      featureCardsHeading:
        "Vi sköter din bortforsling {{kommun}}",
      howItWorksHeading: "Boka din bortforsling {{kommun}} i 3 enkla steg",
      howItWorksSteps: steps,
      serviceCategoriesHeading: "Saker vi bortforslar {{kommun}}",
      serviceCategoriesItems: categories,
      valuePropHeading: "Bortforsling {{kommun}} prislista",
      valuePropRows: pricing,
      priceEstimateSubtitle:
        "Ange dina adresser för att se priser och boka din bortforsling {{kommun}}",
      availableAreasHeading:
        "Få hjälp med bortforsling i dessa kommuner",
      serviceInfoHeading: "Bortforsling {{kommun}}",
      serviceInfoSections: infoSections,
      serviceFAQHeading: "Frågor och svar - Bortforsling {{kommun}}",
      serviceFAQItems: faqs,
      metaTitle:
        "Bortforsling {{kommun}} – Snabb & Smidig | Ungdomskraft",
      metaDescription:
        "Boka bortforsling {{kommun}} av möbler, vitvaror och skrymmande föremål. Fast pris, enkel bokning. Ungdomskraft – över 50 000 slutförda uppdrag.",
    },
  ],
};
